import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNaN from 'lodash/isNaN';
import trim from 'lodash/trim';
import { v4 as uuid } from 'uuid';

import { IValueLabel } from '@interfaces/common/common';

import { ESuccessCodes } from '@enums/successCodes';

import { userBgColors } from '@constants/colors';
import { DEFAULT_5G_TECHNOLOGY, ORAN_5G_TECHNOLOGY, YEAR_RANGE } from '@constants/common';

export {
	cloneDeep,
	debounce,
	isBoolean,
	isEmpty,
	isEqual,
	isNaN,
	trim,
	uuid,
};

export const isNotEmpty = <T>(obj: any): obj is T => {
	return !isEmpty(obj);
};

export function getHash<T>(data: T): number {
	const str = JSON.stringify(data);

	let hash = 0;
	const len = str.length;

	for (let i = 0; i < len; i++) {
		hash = ((hash << 5) - hash) + str.charCodeAt(i);

		// to 32bit integer
		hash |= 0;
	}

	return Math.abs(hash);
}

type TGetAvatarColor = (_str: string, _id?: string | number) => string;

export const getAvatarColor: TGetAvatarColor = (str, id): string => {
	const ID = id
		? typeof id === 'string'
			? getHash(id)
			: id
		: 1;
	let hashCode = 0;

	for (let i = 0; i < str.length; i++) {
		hashCode = str.charCodeAt(i) + ((hashCode << ID) - ID);
		hashCode &= hashCode;
	}

	hashCode = ((hashCode % userBgColors.length) + userBgColors.length) % userBgColors.length;

	return userBgColors[hashCode];
};

export const getYearOptions = (): IValueLabel<number>[] => {
	const minYear = Math.floor(new Date().getFullYear() - YEAR_RANGE / 2);
	const maxYear = minYear + YEAR_RANGE;
	const yearOptions = [];

	for (let i = minYear; i <= maxYear; i++) {
		yearOptions.push({
			value: i,
			label: i,
		});
	}

	return yearOptions;
};

export const shortenNumber = (n?: number | null): string | undefined => {
	if (!n) return;

	const number = new Intl.NumberFormat('en-GB', { notation: 'compact', compactDisplay: 'short' }).format(n);

	return number.toLowerCase();
};

export function sortObjectByKey<T extends Record<string, any>>(
	arrayOfObjects: Array<T>,
	key: string,
): Array<T> {
	if (isEmpty(arrayOfObjects) || !arrayOfObjects) return [];
	if (!key) return arrayOfObjects;

	const clonedArrayOfObjects = cloneDeep(arrayOfObjects);

	return clonedArrayOfObjects.sort((a, b) => {
		if (!a[key] || !b[key]) return arrayOfObjects;

		return a[key].localeCompare(b[key]);
	});
}

export function normalizeObject<T>(payload: T, options?: { removeFalsyBoolean?: boolean }): T {
	const clonedObj = cloneDeep(payload);

	(function clean(object: T) {
		Object
			.entries(object as any)
			.forEach(([key, value]) => {
				if (value && typeof value === 'object') clean(value as any);
				if (value && typeof value === 'object' && !Object.keys(value).length || value === null || value === undefined || value === '') {
					if (Array.isArray(object)) (object as any).splice(key, 1);
					else delete (object as any)[key];
				} else if (options?.removeFalsyBoolean && typeof value === 'boolean' && !value) {
					delete (object as any)[key];
				}
			});

		return object;
	})(clonedObj);

	return clonedObj;
}

export const checkIsRequestAccepted = (response?: { data: any; status: any }): boolean | undefined => {
	return response && response?.status == ESuccessCodes.OK;
};

type TQueryKeyParams<T> = { queryKey: [string, T, { __orgContext: string }?] };

export const extractQueryParams = <T, E>(fn: (data: T) => E) => {
	return ({ queryKey: [_, params] }: TQueryKeyParams<T>) => {
		return fn(params);
	};
};

export const get5GLabel = (isOran: boolean): string => {
	return isOran ? ORAN_5G_TECHNOLOGY : DEFAULT_5G_TECHNOLOGY;
};
